import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import NonAuthLayout from "../../Layout/NonAuthLayout";
import {
  ArrowLeft,
  Image as ImageIcon,
  Hash,
  AlertCircle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { GetSingleEnquiryThunk } from "../../redux/states/enquiries/thunk";

const ViewEnquiries = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { singleEnquiry, loading, error } = useSelector(
    (state) => state.enquiries
  );
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    dispatch(GetSingleEnquiryThunk(uuid));
  }, [dispatch, uuid]);

  if (loading) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-lg font-semibold text-blue-600">
              Loading Enquiries details...
            </p>
            <p className="text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  if (!singleEnquiry) {
    return (
      <NonAuthLayout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
          <div className="text-center bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Enquiries Not Found
            </h2>
            <p className="text-gray-600 mb-6">
              The Enquiries you are looking for is not available.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold flex items-center gap-2 mx-auto transition-all duration-300 transform hover:scale-105"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Enquiries List
            </button>
          </div>
        </div>
      </NonAuthLayout>
    );
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return {
          color: "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };
      case "inactive":
        return {
          color: "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
        };
      default:
        return {
          color: "bg-gray-100 text-gray-800 border-gray-200",
          icon: AlertCircle,
        };
    }
  };

  const StatusBadge = getStatusBadge(singleEnquiry.status);
  const StatusIcon = StatusBadge.icon;

  return (
    <NonAuthLayout>
      <div className="min-h-screen py-2 px-4 sm:px-2 lg:px-1">
        <div className="max-w-12xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 mb-2">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div className="mb-4 lg:mb-0">
                <div className="flex items-center gap-3 mb-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <h1 className="text-3xl font-bold text-gray-800">
                    {singleEnquiry.name}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold border flex items-center gap-1 ${StatusBadge.color}`}
                  >
                    <StatusIcon className="w-4 h-4" />
                    {singleEnquiry.status}
                  </span>
                </div>
                <p className="text-gray-600 flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  SKU: {singleEnquiry.sku} | UUID: {singleEnquiry.uuid}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
                <h2 className="text-2xl font-bold text-gray-800 mb-3">
                  Enquiry Details
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <p className="text-gray-700">
                    <strong>Name:</strong> {singleEnquiry.name || "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Email:</strong>{" "}
                    {singleEnquiry.email || "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Phone:</strong>{" "}
                    {singleEnquiry.phone || "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Product:</strong>{" "}
                    {singleEnquiry.product_id_data || "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Quantity:</strong>{" "}
                    {singleEnquiry.quantity || "Not defined"}{" "}
                    {singleEnquiry.preferred_unit || ""}
                  </p>
                  <p className="text-gray-700">
                    <strong>Price Expectation:</strong>{" "}
                    {singleEnquiry.price_expectation
                      ? `₹${singleEnquiry.price_expectation}`
                      : "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Work Status:</strong>{" "}
                    {singleEnquiry.work_status || "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Status:</strong>{" "}
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        singleEnquiry.status === "active"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {singleEnquiry.status}
                    </span>
                  </p>
                  <p className="text-gray-700 col-span-full">
                    <strong>Message:</strong>{" "}
                    {singleEnquiry.message || "No message provided"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Created At:</strong>{" "}
                    {singleEnquiry.created_at || "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>Updated At:</strong>{" "}
                    {singleEnquiry.updated_at || "Not defined"}
                  </p>
                  <p className="text-gray-700">
                    <strong>UUID:</strong> {singleEnquiry.uuid}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </NonAuthLayout>
  );
};

/* -------------------- Helper Components -------------------- */

export default ViewEnquiries;
